import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { Wordmark } from "@/components/Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, value, testId }) => (
  <div className="rounded-2xl bg-gooi-sand p-6">
    <p data-testid={testId} className="font-display text-4xl font-semibold tracking-tight text-gooi-green">
      {value}
    </p>
    <p className="mt-1 text-sm text-gooi-green/60">{label}</p>
  </div>
);

const Stats = () => {
  const [params] = useSearchParams();
  const [key, setKey] = useState(params.get("sleutel") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    if (!k) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/analytics/summary?key=${encodeURIComponent(k)}`);
      if (!res.ok) throw new Error("Ongeldige sleutel");
      setData(await res.json());
    } catch (e) {
      setError(e.message || "Ophalen mislukt");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Statistieken | Rust langs de A1";
    if (params.get("sleutel")) load(params.get("sleutel"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gooi-offwhite">
      <header className="border-b border-gooi-green/10">
        <div className="mx-auto flex max-w-[90rem] items-center justify-between px-5 py-5 md:px-12">
          <Wordmark />
          <Link
            to="/"
            data-testid="stats-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-gooi-green/80 hover:text-gooi-green"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
            Terug
          </Link>
        </div>
      </header>
      <main data-testid="stats-page" className="mx-auto max-w-[90rem] px-5 py-16 md:px-12">
        <h1 className="font-display text-4xl font-medium tracking-tight text-gooi-green md:text-5xl">
          Campagnestatistieken
        </h1>
        <p className="mt-3 max-w-xl text-sm text-gooi-green/60">
          Alleen voor het campagneteam. Cookievrij gemeten: geen cookies, geen IP-adressen, geen profielen.
        </p>

        <form
          className="mt-8 flex max-w-md gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            load(key);
          }}
        >
          <input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            data-testid="stats-key-input"
            placeholder="Toegangssleutel"
            aria-label="Toegangssleutel"
            className="min-h-[48px] flex-1 rounded-xl border border-gooi-green/20 bg-white px-4 text-base text-gooi-green focus:border-gooi-green focus:outline-none"
          />
          <button
            type="submit"
            data-testid="stats-load-button"
            className="inline-flex min-h-[48px] items-center gap-2 rounded-full bg-gooi-green px-6 font-display text-sm font-semibold text-gooi-offwhite transition-colors duration-300 hover:bg-gooi-forest"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} aria-hidden="true" />
            Toon
          </button>
        </form>
        {error && (
          <p data-testid="stats-error" className="mt-4 text-sm font-semibold text-gooi-red">
            {error}
          </p>
        )}

        {data && (
          <div className="mt-12">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <StatCard label="Paginaweergaven" value={data.totals.page_views} testId="stats-pageviews" />
              <StatCard label="Kliks op petitie" value={data.totals.petition_clicks} testId="stats-petition-clicks" />
              <StatCard label="Deel-acties" value={data.totals.share_clicks} testId="stats-share-clicks" />
              <StatCard label="Contactberichten" value={data.totals.contact_messages} testId="stats-contact" />
            </div>

            <div className="mt-10 grid gap-8 lg:grid-cols-2">
              <section className="rounded-2xl border border-gooi-green/15 p-6">
                <h2 className="font-display text-lg font-medium tracking-tight text-gooi-green">
                  Hero A/B-test
                </h2>
                <p className="mt-1 text-xs text-gooi-green/55">
                  Elke bezoeker ziet willekeurig variant A, B of C. Conversie = petitie-kliks ÷ paginaweergaven.
                </p>
                <table data-testid="stats-variant-table" className="mt-4 w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-gooi-green/15 text-xs uppercase tracking-[0.15em] text-gooi-green/50">
                      <th className="py-2">Variant</th>
                      <th className="py-2 text-right">Weergaven</th>
                      <th className="py-2 text-right">Kliks</th>
                      <th className="py-2 text-right">Conversie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {["A", "B", "C"].map((v) => {
                      const views = data.page_views_by_variant.find((r) => r.variant === v)?.count || 0;
                      const clicks = data.petition_clicks_by_variant.find((r) => r.variant === v)?.count || 0;
                      const conv = views > 0 ? `${Math.round((clicks / views) * 100)}%` : "—";
                      return (
                        <tr key={v} className="border-b border-gooi-green/10 last:border-0">
                          <td className="py-2.5 font-semibold text-gooi-green">Variant {v}</td>
                          <td className="py-2.5 text-right text-gooi-green/70">{views}</td>
                          <td className="py-2.5 text-right text-gooi-green/70">{clicks}</td>
                          <td className="py-2.5 text-right font-semibold text-gooi-forest">{conv}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </section>

              <section className="rounded-2xl border border-gooi-green/15 p-6">
                <h2 className="font-display text-lg font-medium tracking-tight text-gooi-green">
                  Petitie-kliks per positie
                </h2>
                <table data-testid="stats-location-table" className="mt-4 w-full text-left text-sm">
                  <tbody>
                    {data.petition_clicks_by_location.map((r) => (
                      <tr key={r.location} className="border-b border-gooi-green/10 last:border-0">
                        <td className="py-2.5 font-semibold text-gooi-green">{r.location}</td>
                        <td className="py-2.5 text-right text-gooi-green/70">{r.count}</td>
                      </tr>
                    ))}
                    {data.petition_clicks_by_location.length === 0 && (
                      <tr><td className="py-2.5 text-gooi-green/50">Nog geen kliks geregistreerd</td></tr>
                    )}
                  </tbody>
                </table>
              </section>

              <section className="rounded-2xl border border-gooi-green/15 p-6">
                <h2 className="font-display text-lg font-medium tracking-tight text-gooi-green">
                  Apparaat &amp; scroll
                </h2>
                <div className="mt-4 grid gap-6 sm:grid-cols-2">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-gooi-green/50">Apparaat</p>
                    <ul className="mt-2 space-y-1 text-sm">
                      {data.page_views_by_device.map((d) => (
                        <li key={d.device} className="flex justify-between">
                          <span className="text-gooi-green">{d.device}</span>
                          <span className="text-gooi-green/70">{d.count}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-gooi-green/50">Scroll-diepte</p>
                    <ul className="mt-2 space-y-1 text-sm">
                      {data.scroll_depth.map((d) => (
                        <li key={d.depth} className="flex justify-between">
                          <span className="text-gooi-green">{d.depth}%</span>
                          <span className="text-gooi-green/70">{d.count}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="mt-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-gooi-green/50">Deelkanalen</p>
                  <ul className="mt-2 space-y-1 text-sm">
                    {data.share_clicks_by_channel.map((c) => (
                      <li key={c.channel} className="flex justify-between">
                        <span className="text-gooi-green">{c.channel}</span>
                        <span className="text-gooi-green/70">{c.count}</span>
                      </li>
                    ))}
                    {data.share_clicks_by_channel.length === 0 && (
                      <li className="text-gooi-green/50">Nog geen deel-acties</li>
                    )}
                  </ul>
                </div>
                <div className="mt-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-gooi-green/50">UTM-bronnen</p>
                  <ul className="mt-2 space-y-1 text-sm">
                    {data.utm_sources.map((u) => (
                      <li key={u.source} className="flex justify-between">
                        <span className="text-gooi-green">{u.source}</span>
                        <span className="text-gooi-green/70">{u.count}</span>
                      </li>
                    ))}
                    {data.utm_sources.length === 0 && (
                      <li className="text-gooi-green/50">Nog geen UTM-verkeer</li>
                    )}
                  </ul>
                </div>
              </section>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Stats;
